import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import OtpInput from 'react-otp-input'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import type { DialogProps } from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'

import TextInputController from '@/components/ui/inputs/TextInputController'

const changePasswordFormValidationSchema = z
  .object({
    password: z.string().min(1),
    passwordConfirmation: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.password && data.password.length > 0 && data.passwordConfirmation !== data.password) {
      ctx.addIssue({
        code: 'custom',
        message: 'formValidation.passwordConfirmation.isNotMatched',
        path: ['passwordConfirmation'],
      })
    }
  })

export type ChangePasswordFormValues = z.infer<typeof changePasswordFormValidationSchema>

type OtpFeedback = 'sent' | 'send-error' | 'verify-error' | null

type ChangePasswordDialogProps = {
  isSubmitting?: boolean
  isSendingOtp?: boolean
  isVerifyingOtp?: boolean
  onClose: () => void
  onSendOtp: () => Promise<boolean>
  onVerifyOtp: (otp: string) => Promise<boolean>
  onSubmit: (values: ChangePasswordFormValues) => void
  open: boolean
}

const verificationCodeLength = 6

export default function ChangePasswordDialog(props: ChangePasswordDialogProps) {
  const { isSendingOtp = false, isSubmitting = false, isVerifyingOtp = false, onClose, onSendOtp, onVerifyOtp, onSubmit, open } = props
  const { t } = useTranslation()
  const sentForOpenRef = useRef(false)
  const [step, setStep] = useState<'otp' | 'password'>('otp')
  const [code, setCode] = useState('')
  const [otpFeedback, setOtpFeedback] = useState<OtpFeedback>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false)
  const isBusy = isSubmitting || isSendingOtp || isVerifyingOtp

  const rhForm = useForm<ChangePasswordFormValues>({
    defaultValues: { password: '', passwordConfirmation: '' },
    resolver: zodResolver(changePasswordFormValidationSchema),
  })

  const handleSendOtp = useCallback(async () => {
    setOtpFeedback(null)
    const sent = await onSendOtp()
    if (sent) {
      setCode('')
      setOtpFeedback('sent')
      return
    }
    setOtpFeedback('send-error')
  }, [onSendOtp])

  useEffect(() => {
    if (!open) {
      sentForOpenRef.current = false
      rhForm.reset()
      setStep('otp')
      setCode('')
      setOtpFeedback(null)
      setShowPassword(false)
      setShowPasswordConfirmation(false)
      return
    }

    if (sentForOpenRef.current) return
    sentForOpenRef.current = true
    rhForm.reset()
    setStep('otp')
    setCode('')
    setOtpFeedback(null)
    setShowPassword(false)
    setShowPasswordConfirmation(false)
    void handleSendOtp()
  }, [handleSendOtp, open, rhForm])

  const handleClose = useCallback(() => {
    if (isBusy) return
    onClose()
  }, [isBusy, onClose])

  const handleDialogClose: DialogProps['onClose'] = (_event, reason) => {
    if (reason === 'backdropClick') return
    handleClose()
  }

  const handleVerifyOtp = async () => {
    if (code.length !== verificationCodeLength) return
    setOtpFeedback(null)
    const ok = await onVerifyOtp(code)
    if (ok) {
      setStep('password')
      return
    }
    setOtpFeedback('verify-error')
  }

  const handleSubmit = rhForm.handleSubmit((values) => {
    onSubmit(values)
  })

  return (
    <Dialog disableEscapeKeyDown={isBusy} fullWidth maxWidth="xs" onClose={handleDialogClose} open={open}>
      <DialogTitle sx={{ alignItems: 'center', display: 'flex', gap: 2, justifyContent: 'space-between', pr: 1.5 }}>
        <Box component="span">
          {t(step === 'otp' ? 'page.configuration.passwordChangeDialog.verificationTitle' : 'page.configuration.passwordChangeDialog.title')}
        </Box>
        <IconButton aria-label={t('common:button.close.label')} disabled={isBusy} edge="end" onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {step === 'otp' && (
            <Stack spacing={2}>
              <Typography variant="body2">{t('page.configuration.passwordChangeDialog.otpStepDescription')}</Typography>
              <Box display="flex" justifyContent="center">
                <OtpInput
                  value={code}
                  onChange={setCode}
                  numInputs={verificationCodeLength}
                  inputType="tel"
                  shouldAutoFocus
                  renderInput={(inputProps) => (
                    <input
                      {...inputProps}
                      style={{
                        width: 40,
                        height: 48,
                        margin: '0 4px',
                        fontSize: 22,
                        textAlign: 'center',
                        border: '1px solid rgba(0,0,0,0.23)',
                        borderRadius: 6,
                      }}
                    />
                  )}
                />
              </Box>
              {otpFeedback === 'verify-error' && <Alert severity="error">{t('page.configuration.passwordChangeDialog.otpVerifyError')}</Alert>}
              {otpFeedback === 'send-error' && <Alert severity="error">{t('page.configuration.passwordChangeDialog.otpSendError')}</Alert>}
              {otpFeedback === 'sent' && <Alert severity="info">{t('page.configuration.passwordChangeDialog.otpSentInfo')}</Alert>}
            </Stack>
          )}
          {step === 'password' && (
            <form onSubmit={handleSubmit}>
              <Stack spacing={4}>
                <Typography variant="body2" color="text.secondary">
                  {t('page.configuration.passwordChangeDialog.passwordStepDescription')}
                </Typography>
                <TextInputController
                  control={rhForm.control}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={t(
                            showPassword
                              ? 'page.configuration.passwordChangeDialog.hidePasswordButton'
                              : 'page.configuration.passwordChangeDialog.showPasswordButton'
                          )}
                          edge="end"
                          onClick={() => setShowPassword((visible) => !visible)}
                          onMouseDown={(event) => event.preventDefault()}
                          size="small"
                        >
                          {showPassword ? <VisibilityOffIcon fontSize="inherit" /> : <VisibilityIcon fontSize="inherit" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  label={t('field.password.label', { context: 'new' })}
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                />
                <TextInputController
                  control={rhForm.control}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={t(
                            showPasswordConfirmation
                              ? 'page.configuration.passwordChangeDialog.hidePasswordButton'
                              : 'page.configuration.passwordChangeDialog.showPasswordButton'
                          )}
                          edge="end"
                          onClick={() => setShowPasswordConfirmation((visible) => !visible)}
                          onMouseDown={(event) => event.preventDefault()}
                          size="small"
                        >
                          {showPasswordConfirmation ? <VisibilityOffIcon fontSize="inherit" /> : <VisibilityIcon fontSize="inherit" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  label={t('field.passwordConfirmation.label', { context: 'new' })}
                  name="passwordConfirmation"
                  type={showPasswordConfirmation ? 'text' : 'password'}
                />
              </Stack>
            </form>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {step === 'otp' && (
          <Button disabled={isSendingOtp || isVerifyingOtp} onClick={handleSendOtp}>
            {t('page.configuration.passwordChangeDialog.resendCodeButton')}
          </Button>
        )}
        <Button disableElevation disabled={isBusy} onClick={handleClose} variant="neutral">
          {t('common:button.cancel.label')}
        </Button>
        {step === 'otp' && (
          <LoadingButton disabled={code.length !== verificationCodeLength} loading={isVerifyingOtp} onClick={handleVerifyOtp} variant="contained">
            {t('common:button.continue.label')}
          </LoadingButton>
        )}
        {step === 'password' && (
          <LoadingButton color="primary" disableElevation loading={isSubmitting} onClick={handleSubmit} variant="contained">
            {t('common:button.submit.label')}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  )
}
